import { api } from '../client';

import type { ListResponse, PaginationParams } from './common';
import {
  rememberResponseItemsUpdatedAt,
  rememberResponseUpdatedAt,
  type StockDocumentMutationDeps,
  type StockDocumentMutationOptions,
} from './documentMutationCache';

export interface StockSupply {
  id: string;
  name: string;
  unit_code: string;
  is_weighted: boolean;
  category_id?: string | null;
  category_name?: string | null;
  default_cost?: string | null;
  is_active: boolean;
  can_hard_delete?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SupplyListSummary {
  by_status: Record<string, number>;
}

export interface StockSuppliesResponse extends ListResponse<StockSupply> {
  summary?: SupplyListSummary | null;
}

export interface StockSupplyDetailResponse {
  request_id: string;
  supply: StockSupply;
}

export interface StockSupplyFilters extends PaginationParams {
  [key: string]: string | number | boolean | undefined;
  is_active?: boolean;
  category_id?: string;
  search?: string;
  supplier_receiptable_only?: boolean;
  include_produced_products?: boolean;
  include_summary?: boolean;
}

export interface CreateStockSupplyRequest {
  name: string;
  unit_code: string;
  category_id?: string | null;
  default_cost?: string | null;
  is_active?: boolean;
}

export interface UpdateStockSupplyRequest {
  name: string;
  unit_code: string;
  category_id: string | null;
  default_cost: string | null;
  is_active: boolean;
}

type SupplyStockEntity = 'supply';

export type StockSuppliesApiDeps = Pick<
  StockDocumentMutationDeps<SupplyStockEntity>,
  'rememberStockDocumentUpdatedAt' | 'withExpectedUpdatedAt'
>;

export const createStockSuppliesApi = ({
  rememberStockDocumentUpdatedAt,
  withExpectedUpdatedAt,
}: StockSuppliesApiDeps) => ({
  list: async (filters: StockSupplyFilters = {}): Promise<StockSuppliesResponse> => {
    return rememberResponseItemsUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'supply',
      await api.webAdmin.get<StockSuppliesResponse>('/stock/supplies', { params: filters }),
      (response) => response.items,
      (item) => item.id,
      (item) => item.updated_at,
    );
  },

  create: async (
    data: CreateStockSupplyRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<StockSupplyDetailResponse> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'supply',
      await api.webAdmin.post<StockSupplyDetailResponse>('/stock/supplies', data, requestOptions),
      (response) => response.supply.id,
      (response) => response.supply.updated_at,
    );
  },

  update: async (
    id: string,
    data: UpdateStockSupplyRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<StockSupplyDetailResponse> => {
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'supply',
      await api.webAdmin.put<StockSupplyDetailResponse>(
        `/stock/supplies/${id}`,
        data,
        withExpectedUpdatedAt('supply', id, options),
      ),
      (response) => response.supply.id,
      (response) => response.supply.updated_at,
    );
  },

  activate: async (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<StockSupplyDetailResponse> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'supply',
      await api.webAdmin.post<StockSupplyDetailResponse>(
        `/stock/supplies/${id}/activate`,
        undefined,
        requestOptions,
      ),
      (response) => response.supply.id,
      (response) => response.supply.updated_at,
    );
  },

  deactivate: async (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<StockSupplyDetailResponse> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'supply',
      await api.webAdmin.post<StockSupplyDetailResponse>(
        `/stock/supplies/${id}/deactivate`,
        undefined,
        requestOptions,
      ),
      (response) => response.supply.id,
      (response) => response.supply.updated_at,
    );
  },

  delete: async (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<{ request_id: string; success: boolean }> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    return api.webAdmin.delete<{ request_id: string; success: boolean }>(
      `/stock/supplies/${id}`,
      requestOptions,
    );
  },
});
